import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { dequal } from "dequal";
import { syncFromURL } from "../../store/slices/userSelection/userSelection";
import { parseURLParams } from "@/utils/url";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import {
  AGE_START,
  BRANDS,
  BUTTERLFY,
  DIFFERENTIATION,
  FILTERS,
  MARKETS,
  MODE,
  PERCEPTUAL,
  PERIOD_END_DATE,
  PERIOD_START_DATE,
  POINT_IN_TIME,
  POST_END_DATE,
  POST_START_DATE,
  PROFILE,
  PROFILE_GROUP,
  PROFILE_RESPONSE,
  PROFILE_METRIC,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  VIEW_ID,
  URL_BY_BRAND,
} from "@/constant/constantsThrougoutProject";
import { ALL_URL_KEYS } from "@/constant/url";
import { getUpdateUrlParameters } from "./utils";
import { OLD_URLS } from "./oldUtils";
import { getNewURL } from "./oldUtils";
import { getFeatureRedirect } from "@/utils/featureStatus";
import { AGE_SEGMENT_LABELS } from "@/constant/ageSegment";
import ShowToaster from "@/toaster";

export const useURLSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialDataUpdated, setIsInitialDataUpdated] =
    useState<boolean>(false);

  const activeGroupDataIds = useSelector(
    (state: RootState) => {
      return {
        brands: state.activeGroup.brands.ids,
        markets: [...state.activeGroup.markets.markets.ids].sort((a, b) => {
          const marketA = state.activeGroup.markets.markets.entities[a];
          const marketB = state.activeGroup.markets.markets.entities[b];

          if (!marketA || !marketB) return 0;

          const aIsUS = marketA.identity.toLowerCase().startsWith("us");
          const bIsUS = marketB.identity.toLowerCase().startsWith("us");

          if (aIsUS && !bIsUS) return -1;
          if (!aIsUS && bIsUS) return 1;

          if (aIsUS && bIsUS) {
            return marketA.identity.length - marketB.identity.length;
          }

          return 0;
        }),
        brandsEntities: state.activeGroup.brands.entities,
        segments: state.activeGroup.segments.segments.ids,
        ageSegmentId: state.activeGroup.segments.segments.ids?.find(
          (segmentId: number) => {
            if (
              AGE_SEGMENT_LABELS?.includes(
                state.activeGroup.segments.segments.entities[segmentId]?.name,
              ) && state.activeGroup.segments.segments.entities[segmentId]?.categoryType === "DEFAULT"
            ) {
              return true;
            }
            return false;
          },
        ),
        activeSegments: state.activeGroup.segments.segments.ids?.filter(
          (segmentId: number) => {
            if (
              state.activeGroup.segments.segments.entities[segmentId]?.isActive
            ) {
              return true;
            }
            return false;
          },
        ),
        filters: state.activeGroup.filters.filters.ids?.reduce(
          (acc: number[], curr: number) => {
            const currentFilter =
              state.activeGroup.filters.filters?.entities[curr];
            const filterOptionsIds = currentFilter?.filterOptions?.map(
              (item) => item.filterOptionId,
            );
            return [...acc, ...filterOptionsIds];
          },
          [],
        ),
      };
    },
    (prev, next) => dequal(prev, next),
  );

  const markets = useSelector(
    (state: RootState) => state.activeGroup.markets.markets,
    (prev, next) => dequal(prev, next),
  );

  const userSelection = useSelector(
    (state: RootState) => state.userSelection,
    (prev, next) => dequal(prev, next),
  );

  const featureStatus = useSelector(
    (state: RootState) => state.featureStatus,
    (prev, next) => dequal(prev, next),
  );

  const { questions: allQuestions, isQuestionsFetchedInitially } = useSelector(
    (state: RootState) => state.activeGroup.questions,
    dequal,
  );

  useEffect(() => {
    if (
      activeGroupDataIds?.brands?.length > 0 &&
      activeGroupDataIds?.markets?.length > 0
    ) {
      if (OLD_URLS?.includes(location.pathname?.toLowerCase())) {
        const { pathname, url } = getNewURL(location.search, markets);
        let updatedParams = parseURLParams(new URLSearchParams(url));
        const urlUpdated = getUpdateUrlParameters(
          pathname,
          updatedParams,
          userSelection,
          activeGroupDataIds,
        );
        setIsInitialDataUpdated(true);
        navigate({
          pathname: pathname,
          search: urlUpdated,
        });
      } else {
        let updatedParams = parseURLParams(
          new URLSearchParams(location.search),
        );
        const redirectUrl = getFeatureRedirect(featureStatus as any);
        const url = getUpdateUrlParameters(
          location.pathname === "/" ? redirectUrl : location.pathname,
          updatedParams,
          userSelection,
          activeGroupDataIds,
        );
        setIsInitialDataUpdated(true);
        navigate({
          pathname: location.pathname === "/" ? redirectUrl : location.pathname,
          search: url,
        });
      }
    }
  }, [activeGroupDataIds]);

  useEffect(() => {
    if (isInitialDataUpdated) {
      let updatedParams = parseURLParams(new URLSearchParams(location.search));
      if (Object.keys(updatedParams)?.length > 0) {
        const currentPathConfig = ROUTES_CHILDREN_LOOKUP[location.pathname];
        const finalParams: Record<string, any> = {};
        let mode: string =
          typeof updatedParams?.mode === "string"
            ? updatedParams?.mode
            : "POINT_IN_TIME";
        if (
          mode === POINT_IN_TIME &&
          (updatedParams?.visualizationType === PERCEPTUAL ||
            updatedParams?.visualizationType === BUTTERLFY ||
            updatedParams?.visualizationType === DIFFERENTIATION ||
            updatedParams?.visualizationType === PROFILE)
        ) {
          mode = updatedParams.visualizationType;
        }
        let ageRange: number[] = [];
        currentPathConfig?.urlKeysMapping?.[
          (currentPathConfig?.modeOptions?.includes(mode) && mode) ||
          "POINT_IN_TIME"
        ]?.forEach((key: string) => {
          let value: any = updatedParams[key];
          if (key === FILTERS || key === SEGMENTS || key === QUESTION_OPTIONS) {
            if (!updatedParams[key]) {
              value = [];
            }
          }
          if (value) {
            if (key === BRANDS || key === MARKETS) {
              if (
                currentPathConfig?.multipleOptionsForKeys[
                  (mode as any) || POINT_IN_TIME
                ]?.includes(key)
              ) {
                finalParams[key] = activeGroupDataIds?.[key]?.filter(
                  (id: number) => value?.includes(id),
                );
              } else {
                finalParams[key.slice(0, key.length - 1)] =
                  value?.length > 0 ? value[0] : "";
              }
            } else if (key === SECONDARY_BRANDS) {
              finalParams[key] = value;
            } else if (
              key === PERIOD_START_DATE ||
              key === PERIOD_END_DATE ||
              key === POST_START_DATE ||
              key === POST_END_DATE
            ) {
              finalParams[key] = value;
            } else if (key === SEGMENTS) {
              if (currentPathConfig?.useSegmentSelect) {
                if (updatedParams?.[key]) {
                  finalParams["activeSegments"] = [...new Set(value)];
                }
              } else {
                if (updatedParams?.[key]) {
                  finalParams[key] = value;
                } else {
                  finalParams[key] = [];
                }
              }
            } else if (key === FILTERS) {
              if (updatedParams?.[key]) {
                finalParams[key] = value.filter(
                  (item: any) => typeof item === "number" && !Number.isNaN(item),
                );
              } else {
                finalParams[key] = [];
              }
            } else if (key === AGE_START) {
              ageRange = [
                Number(updatedParams?.ageStart),
                Number(updatedParams?.ageEnd),
              ].filter((item) => item > 0);
            } else if (key === MODE) {
              if (currentPathConfig?.modeOptions?.includes(value)) {
                finalParams[key] = value;
              } else {
                finalParams[key] = POINT_IN_TIME;
              }
            } else if (key === QUESTION_OPTIONS) {
              const result: Record<string, number[]> = {};
              value?.forEach((val: any) => {
                const currVal = val?.split("-");
                if (currVal?.length > 1) {
                  if (result[currVal[0]]) {
                    result[currVal[0]] = [
                      ...result[currVal[0]],
                      Number(currVal[1]),
                    ];
                  } else {
                    result[currVal[0]] = [Number(currVal[1])];
                  }
                }
              });
              finalParams[key] = result;
            } else if (
              key === PROFILE_GROUP ||
              key === PROFILE_METRIC ||
              key === PROFILE_RESPONSE
            ) {
              finalParams[key] = !isNaN(value) ? Number(value) : value;
            } else if (key === TRENDS_METRIC || key === TRENDS_RESPONSE) {
              finalParams[key] = value;
            } else if (key === VIEW_ID) {
              finalParams[key] = value;
            } else if (ALL_URL_KEYS?.includes(key)) {
              finalParams[key] = value;
            }
          }
        });
        if (location.pathname.startsWith("/reputation")) {
          //based on tab check keys for brands and markets 
          const marketIds = finalParams.market
            ? [finalParams.market]
            : finalParams.markets || [];
          const brandIds = finalParams.brand 
            ? [finalParams.brand] 
            : finalParams.brands || [];

          if (marketIds?.length > 0 && brandIds?.length > 0) {
            const reputationValidBrandIds = new Set<number>();
            Object.values(activeGroupDataIds.brandsEntities).forEach(
              (brand) => {
                if (brand) {
                  const reputationMarketIds = (brand as any).reputationMarketIds;
                  if (
                    Array.isArray(reputationMarketIds) &&
                    marketIds.some((mid: number) =>
                      reputationMarketIds.includes(mid)
                    )
                  ) {
                    reputationValidBrandIds.add(brand.id);
                  }
                }
              }
            );

            let validBrandIds = brandIds.filter((id: number) =>
              reputationValidBrandIds.has(id)
            );

            if (validBrandIds.length === 0 && reputationValidBrandIds.size > 0) {
              validBrandIds = [Array.from(reputationValidBrandIds)[0]];
            }
            finalParams.brands = validBrandIds;
          }
        }
        dispatch(
          syncFromURL({
            ...finalParams,
            ageRange: ageRange?.length > 1 ? ageRange : null,
          }),
        );
      }
    }
  }, [navigate, location]);

  useEffect(() => {
    if (isQuestionsFetchedInitially) {
      const params = new URLSearchParams(location.search);
      const questionOptionsFromURL = params.getAll(QUESTION_OPTIONS);

      if (questionOptionsFromURL.length > 0) {
        const validQuestionOptions = questionOptionsFromURL.filter((option) => {
          const [questionKey] = option.split("-");
          return allQuestions.entities[questionKey];
        });

        const inValidQuestionKeys: string[] = [
          ...new Set(
            questionOptionsFromURL
              .map((option) => option.split("-")[0])
              .filter((questionKey) => !allQuestions.entities[questionKey])
          ),
        ];

        if (inValidQuestionKeys?.length > 0) {
          const message: string = `${inValidQuestionKeys.length > 1 ? 'questions are' : 'question is'} not applicable for the selected market.`
          // ShowToaster.info(inValidQuestionKeys?.join(',') + ' ' + message)
        }

        if (validQuestionOptions.length < questionOptionsFromURL.length) {
          const newParams = new URLSearchParams(location.search);
          newParams.delete(QUESTION_OPTIONS);
          validQuestionOptions.forEach((option) => {
            newParams.append(QUESTION_OPTIONS, option);
          });
          navigate({ search: newParams.toString() }, { replace: true });
        }
      }
    }
  }, [isQuestionsFetchedInitially, allQuestions.entities, location, navigate]);
};

export default useURLSync;
